import { ChargeForecast } from '@/types'
import {
  getEndChargeDatetime,
  getIsCharging,
  getStartChargeDatetime,
  getUpcomingDREventTimeline,
} from '../utils'
import { Text } from '@/components'
import { Mode } from '.'
import {
  getRelativeDay,
  isPast,
  getAbsoluteDiff,
  getChargeBoundsMetadata,
} from '@/types/chargeForecast/utils'
import { useDistanceUnit } from '@/authenticated/hooks/conversions'
import dayjs from 'dayjs'
import { match } from 'ts-pattern'

type ChargeScheduleTimeProps = {
  mode: Exclude<Mode, 'NoCharge'>
  chargeForecast: ChargeForecast
}

type PastTime = dayjs.Dayjs

type FutureTime = dayjs.Dayjs

type InProcessTimeRange = {
  startTime: PastTime
  endTime?: FutureTime | null
  type: 'InProcess'
}

type FutureTimeRange = {
  startTime: FutureTime
  endTime: FutureTime
  type: 'Future'
}

type InvalidTimeRange = {
  type: 'Invalid'
}

type ValidateTimeProps = {
  startTime: PastTime | FutureTime
  endTime?: FutureTime | null
}

type ValidateTimeReturnType =
  | InProcessTimeRange
  | FutureTimeRange
  | InvalidTimeRange

//end time can be optional
const validateTime = ({
  startTime,
  endTime,
}: ValidateTimeProps): ValidateTimeReturnType => {
  if (endTime && endTime.isBefore(startTime)) {
    return { type: 'Invalid' } as InvalidTimeRange
  }
  if (endTime && endTime.isBefore(dayjs())) {
    return { type: 'Invalid' } as InvalidTimeRange
  }
  if (isPast(startTime)) {
    return { startTime, endTime, type: 'InProcess' } as InProcessTimeRange
  } else {
    return { startTime, endTime, type: 'Future' } as FutureTimeRange
  }
}

type ChargeScheduleTimeValidReturnType = {
  chargeStartDatetime: dayjs.Dayjs
  chargeEndDatetime?: dayjs.Dayjs | null
  chargeDateTimeDiff: number
  startTimeLabel: string
  endTimeLabel: string
  startPercent?: number
  endPercent?: number
  startDistance?: number
  endDistance?: number
  distanceSuffix: string
}

type ChargeScheduleTimeInvalidReturnType = null

type ChargeScheduleTimeReturnType =
  | ChargeScheduleTimeValidReturnType
  | ChargeScheduleTimeInvalidReturnType

export function useViewModel({
  mode,
  chargeForecast,
}: ChargeScheduleTimeProps): ChargeScheduleTimeReturnType {
  const [userPreferredDistanceUnit] = useDistanceUnit()
  const drEventTimeline = getUpcomingDREventTimeline(chargeForecast)
  const isCharging = getIsCharging(chargeForecast)

  const chargeStartDatetime =
    mode === 'DREventScheduled'
      ? drEventTimeline?.start
      : getStartChargeDatetime(chargeForecast)
  const chargeEndDatetime =
    mode === 'DREventScheduled'
      ? drEventTimeline?.end
      : getEndChargeDatetime(chargeForecast)

  // This can be shown with no end time as long as a start time exists
  if (!chargeStartDatetime) return null

  const validatedTimes = validateTime({
    startTime: chargeStartDatetime,
    endTime: chargeEndDatetime,
  })
  if (validatedTimes.type === 'Invalid') return null
  // return null if currently charging and start time is in the future
  // Not a valid charge forecast
  if (isCharging && validatedTimes.type === 'Future') return null

  const startTense = match({ type: validatedTimes.type, isCharging })
    .with({ type: 'InProcess', isCharging: true }, () => 'Started')
    .with({ type: 'InProcess', isCharging: false }, () => 'Starts')
    .with({ type: 'Future' }, () => 'Starts')
    .with({ type: 'InProcess' }, () => 'Started')
    .otherwise(() => 'Starts')

  const relativeStart = getRelativeDay(validatedTimes.startTime)
  const startTimeLabel =
    mode === 'DREventScheduled'
      ? 'Event begin'
      : `${startTense} ${relativeStart}`

  const endTense = 'Ends'
  const relativeEnd = getRelativeDay(chargeEndDatetime)
  const endTimeLabel =
    mode === 'DREventScheduled' ? 'Event end' : `${endTense} ${relativeEnd}`

  const chargeDateTimeDiff = chargeEndDatetime
    ? getAbsoluteDiff(chargeEndDatetime, dayjs())
    : 0

  const chargeBoundsMetadata = getChargeBoundsMetadata(
    chargeForecast,
    userPreferredDistanceUnit,
  )
  if (chargeBoundsMetadata === null && mode !== 'DREventScheduled') return null

  const distanceSuffix = userPreferredDistanceUnit === 'miles' ? 'mi' : 'km'

  const props: ChargeScheduleTimeValidReturnType = {
    chargeStartDatetime,
    chargeEndDatetime,
    chargeDateTimeDiff,
    startTimeLabel,
    endTimeLabel,
    startPercent: chargeBoundsMetadata?.startPercent,
    endPercent: chargeBoundsMetadata?.endPercent,
    startDistance: chargeBoundsMetadata?.startDistance,
    endDistance: chargeBoundsMetadata?.endDistance,
    distanceSuffix,
  }
  return props
}

export default function ChargeScheduleTime(props: ChargeScheduleTimeProps) {
  const viewModel = useViewModel(props)
  if (!viewModel) return null
  const {
    chargeStartDatetime,
    chargeEndDatetime,
    chargeDateTimeDiff,
    startTimeLabel,
    endTimeLabel,
    startPercent,
    endPercent,
    startDistance,
    endDistance,
    distanceSuffix,
  } = viewModel

  return (
    <>
      <div className="flex flex-col flex-1">
        <Text variant="body2">{startTimeLabel}</Text>
        <div className="flex items-end">
          <Text variant="h1">{chargeStartDatetime.format('h:mm')}</Text>
          <Text variant="body1" className="pb-1 pl-1">
            {chargeStartDatetime.format('A')}
          </Text>
        </div>
        <div className="flex">
          {startPercent && startDistance && (
            <Text className="font-medium" variant="body2">
              {Math.round(startPercent)}% • {Math.round(startDistance)}{' '}
              {distanceSuffix}
            </Text>
          )}
        </div>
      </div>
      {chargeEndDatetime && (
        <div className="flex flex-col flex-1">
          <Text variant="body2">{endTimeLabel}</Text>
          <div className="flex items-end">
            <Text variant="h1">{chargeEndDatetime.format('h:mm')}</Text>
            <Text variant="body1" className="pb-1 pl-1">
              {chargeEndDatetime.format('A')}
            </Text>
            {chargeDateTimeDiff !== 0 && (
              <Text
                className="pt-1 pl-1 mb-auto"
                variant="body1"
              >{`+${chargeDateTimeDiff}`}</Text>
            )}
          </div>
          <div className="flex">
            {endPercent && endDistance && (
              <Text className="font-medium" variant="body2">
                {Math.round(endPercent)}% • {Math.round(endDistance)}{' '}
                {distanceSuffix}
              </Text>
            )}
          </div>
        </div>
      )}
    </>
  )
}
